/**
 * Hooks de mutación de **sedes** (alta, edición, borrado).
 *
 * Cada mutación, en éxito, **invalida las queries afectadas** (`docs/
 * frontend_conventions.md`, Datos): la raíz `['admin-sedes']` cubre la lista (con
 * stats) y los detalles. El gating de permiso (`all`) lo decide la UI; aquí solo
 * se ejecuta y se propaga el `ApiError` (la pantalla lo traduce con
 * `adminErrorMessage`).
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { createSede, deleteSede, updateSede } from "../api";
import type { CreateSedeBody, SedeDetail, UpdateSedeBody } from "../types";
import { SEDES_ROOT_KEY } from "./queryKeys";

/** Invalida toda la rama de sedes (lista con stats + detalles). */
function useInvalidateSedes() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: SEDES_ROOT_KEY });
  };
}

/** Alta de sede (`POST /sedes`). */
export function useCreateSede() {
  const invalidate = useInvalidateSedes();
  return useMutation<SedeDetail, Error, CreateSedeBody>({
    mutationFn: (body) => createSede(body),
    onSuccess: invalidate,
  });
}

/** Argumentos de edición: el id de la sede + los cambios. */
interface UpdateSedeArgs {
  sedeId: number;
  body: UpdateSedeBody;
}

/** Edición de sede (`PATCH /sedes/<id>`). */
export function useUpdateSede() {
  const invalidate = useInvalidateSedes();
  return useMutation<SedeDetail, Error, UpdateSedeArgs>({
    mutationFn: ({ sedeId, body }) => updateSede(sedeId, body),
    onSuccess: invalidate,
  });
}

/** Borrado de sede (`DELETE /sedes/<id>`). */
export function useDeleteSede() {
  const invalidate = useInvalidateSedes();
  return useMutation<void, Error, number>({
    mutationFn: (sedeId) => deleteSede(sedeId),
    onSuccess: invalidate,
  });
}
